import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const Privacy = () => {
  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <Sparkles className="h-8 w-8 text-primary" />
          <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
            Privacy Policy
          </h1>
        </div>

        <div className="prose prose-invert max-w-none space-y-6">
          <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">1. Information We Collect</h2>
            <p className="text-muted-foreground">We collect information you provide directly:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Account Information:</strong> Name, email address, password, profile photo</li>
              <li><strong>Profile Information:</strong> Bio, location, skills, portfolio content, social media links</li>
              <li><strong>Payment Information:</strong> Processed securely through third-party providers (Stripe)</li>
              <li><strong>Communications:</strong> Messages between users, support requests</li>
              <li><strong>Usage Data:</strong> How you interact with our platform</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">2. How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>To provide and improve our services</li>
              <li>To process bookings and payments</li>
              <li>To enable communication between users</li>
              <li>To send service-related notifications</li>
              <li>To ensure platform security and prevent fraud</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">3. Information Sharing</h2>
            <p className="text-muted-foreground">We may share your information with:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Other Users:</strong> Profile information is visible to other platform users</li>
              <li><strong>Service Providers:</strong> Payment processors, hosting providers, analytics services</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">4. Data Security</h2>
            <p className="text-muted-foreground">
              We implement industry-standard security measures to protect your data, including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure password hashing</li>
              <li>Regular security audits</li>
              <li>Access controls and authentication</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">5. Your Rights</h2>
            <p className="text-muted-foreground">You have the right to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate information</li>
              <li>Delete your account and associated data</li>
              <li>Export your data</li>
              <li>Opt out of marketing communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">6. Cookies and Tracking</h2>
            <p className="text-muted-foreground">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Keep you logged in</li>
              <li>Remember your preferences</li>
              <li>Analyze platform usage</li>
              <li>Improve user experience</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">7. Third-Party Services</h2>
            <p className="text-muted-foreground">
              Our platform integrates with third-party services including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Stripe for payment processing</li>
              <li>Google for authentication (optional)</li>
              <li>OpenAI for AI-powered features</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              These services have their own privacy policies governing data they collect.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">8. Data Retention</h2>
            <p className="text-muted-foreground">
              We retain your data for as long as your account is active or as needed to provide services. 
              You can request deletion of your data at any time by contacting support or deleting your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">9. Children's Privacy</h2>
            <p className="text-muted-foreground">
              Our platform is not intended for users under 18 years of age. We do not knowingly 
              collect information from children under 18.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">10. Changes to This Policy</h2>
            <p className="text-muted-foreground">
              We may update this Privacy Policy periodically. We will notify users of significant 
              changes via email or platform notification.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">11. Contact Us</h2>
            <p className="text-muted-foreground">
              If you have questions about this Privacy Policy or your data, please contact us 
              through the platform's support channels.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-border">
          <Link to="/terms">
            <Button variant="outline" className="rounded-full">
              View Terms of Service
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
